import { Config } from '@backstage/config';
import { OAuthApi } from '@backstage/core-plugin-api';
import { getUserAccessToken } from './azureTokenManager';
import {
  ApiError,
  OndemandAdminRequestResult,
  OndemandAdminRequestStatus,
  OndemandAdminWorkloadResult,
  getRequestStatusPayload,
  requestOndemandAdminPayload,
} from './OndemandAdminClientTypes';

export interface workloadResponse<T> {
  response: Response;
  json: () => Promise<T>;
}

const getJson = async <T,>(response: Response): Promise<T> =>
  (await response.json()) as T;

export class OndemandAdminClient {
  private readonly backendBaseUrl: string;
  private readonly oauthAPI: OAuthApi;

  constructor(config: Config, oauthAPI: OAuthApi) {
    this.backendBaseUrl = config.get('backend.baseUrl');
    this.oauthAPI = oauthAPI;
  }

  async requestOndemandAdmin({
    accountId,
    requesterEmail,
    workloadName,
    justification,
  }: requestOndemandAdminPayload): Promise<
    workloadResponse<OndemandAdminRequestResult>
  > {
    const payload = {
      requester: requesterEmail,
      workloadName: workloadName,
      accountId: accountId,
      justification: justification,
    };

    const url = `${workloadName}/${accountId}`;

    const response = await this.apiFetch(url, 'POST', payload);

    return {
      response,
      json: () => getJson<OndemandAdminRequestResult>(response),
    };
  }

  async getRequestStatus({
    workloadName,
    accountId,
    requestId,
  }: getRequestStatusPayload): Promise<
    workloadResponse<OndemandAdminRequestStatus | ApiError>
  > {
    const url = [workloadName, accountId, 'requests', requestId]
      .map(encodeURIComponent)
      .join('/');

    const response = await this.apiFetch(url);

    return {
      response,
      json: () => getJson<OndemandAdminRequestStatus | ApiError>(response),
    };
  }

  async fetchOndemandAdminWorkloadData(
    workloadName: string,
  ): Promise<workloadResponse<OndemandAdminWorkloadResult>> {
    const response = await this.apiFetch(`${workloadName}`);

    return {
      response,
      json: () => getJson<OndemandAdminWorkloadResult>(response),
    };
  }

  async apiFetch(
    resource: string,
    method?: string,
    body?: any,
  ): Promise<Response> {
    let method_to_call = method;
    if (typeof method === 'undefined') {
      method_to_call = 'GET';
    }

    const userToken = await getUserAccessToken(
      this.oauthAPI,
      getAudForEnv(),
      getDefaultUserScopes(),
    );
    const options: RequestInit = {
      method: method_to_call,
      headers: { Authorization: `Bearer ${userToken}` },
    };
    if (body) {
      options.body = JSON.stringify(body);
    }

    return await fetch(
      `${this.backendBaseUrl}/api/proxy/aws-ondemand-admin-auth/${resource}`,
      options,
    );
  }
}

export function getDefaultUserScopes(): string[] {
  const ApiAud = getAudForEnv();
  return [`api://${ApiAud}/admin:session:request`];
}

export function getAudForEnv(): string {
  const currURL = window.location.origin;

  if (
    currURL.indexOf('staging.') < 0 &&
    currURL.indexOf('dev.') < 0 &&
    currURL.indexOf('int.') < 0 &&
    currURL.indexOf('localhost') < 0
  ) {
    return 'isr-aws-ondemand-admin';
  }
  return 'isr-aws-ondemand-admin.test';
}
